import { Button } from '@design-system/components/button/button';
import { Modal, PlainModalProps } from '@design-system/components/modal/modal';
import { Typography } from '@design-system/components/typography/typography';
import { faRocket } from '@fortawesome/pro-solid-svg-icons';
import GoLiveSubscription from './go-live-subscription.png';
import GoLiveOtp from './go-live-otp.png';
import GoLiveBoth from './go-live-both.png';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { CollectionType } from '../home.domain';

type GoLiveModalProps = Pick<PlainModalProps, 'isOpen' | 'onClose'> & {
  version: CollectionType | 'both';
  goLive: () => void;
  inProgress: boolean;
};

export const GoLiveModal = (props: GoLiveModalProps) => (
  <Modal
    {...props}
    modalClassName="w-full md:w-[600px]"
    contentClassName="w-full h-full gap-0"
    title="🚀 Ready to Go Live ?"
    largeTitle
    centerContent
    disableBackdropClick
  >
    <div className="flex flex-col gap-8 items-center justify-center mt-8">
      <div className="flex flex-col gap-4 items-center justify-center">
        <VersionContent version={props.version} />
        <Typography
          color="ERROR-100"
          variant="body"
          size="medium"
          fontWeight="normal"
        >
          🚨 Go Live cannot be undone
        </Typography>
      </div>
      <div className="flex gap-6 justify-center items-center">
        <Button
          color="neutral"
          onClick={props.onClose}
          disabled={props.inProgress}
        >
          Not Yet
        </Button>
        <Button
          color="success"
          leftIcon={faRocket}
          onClick={props.goLive}
          disabled={props.inProgress}
          loading={props.inProgress}
        >
          Go Live Now
        </Button>
      </div>
    </div>
  </Modal>
);

const VersionContent = ({ version }: { version: CollectionType | 'both' }) => {
  switch (version) {
    case 'otp':
      return <OtpContent />;
    case 'subscription':
      return <SubscriptionContent />;
    case 'both':
      return <BothContent />;
  }
};

const OtpContent = () => (
  <>
    <Typography variant="body" size="large" fontWeight="normal">
      Going live means you'll be able to start selling your OTP Product that are
      "Published."
    </Typography>
    <img src={GoLiveOtp} className="max-h-[265px]" />
    <Typography variant="body" size="large" fontWeight="normal">
      All published OTPs will automatically be visible on the platform. Leave an
      OTP as a Draft if you don't want it to be on sale yet
    </Typography>
  </>
);

const SubscriptionContent = () => (
  <>
    <Typography variant="body" size="large" fontWeight="normal">
      Going live means you can start selling your Subscription! See what will be
      visible to your audience after your launch
    </Typography>
    <img src={GoLiveSubscription} className="max-h-[265px]" />
    <Typography variant="body" size="large" fontWeight="normal">
      From now on, all newly published Programs & Categories will be
      automatically visible and available to your subscribers.
      <a
        className={clsx('font-inherit underline', sprinkles({ color: 'P-80' }))}
        href="https://help.playbookapp.io/en/articles/9693285-subscription-business-what-a-successful-experience-looks-like"
        target="_blank"
        rel="noreferrer"
      >
        Watch this video
      </a>{' '}
      for tips to maximize your subscription strategy and boost sales. 🚀
    </Typography>
  </>
);

const BothContent = () => (
  <>
    <Typography variant="body" size="large" fontWeight="normal">
      You've got programs and categories published in both One-Time Purchases
      and Subscription-amazing! 😍{' '}
      <b>Going live means both will be visible and ready to sell.</b>
    </Typography>
    <img src={GoLiveBoth} className="max-h-[265px]" />
  </>
);
